import { CompoundedComponent, IFormControl } from "@/types"
import { cn, getFormattedPhone } from "@/utils"
import { clsx } from "clsx"
import { isValidPhoneNumber } from "libphonenumber-js"
import { forwardRef, InputHTMLAttributes, useMemo, useState } from "react"
import { Controller, FieldValues, Path, RegisterOptions, useFormContext } from "react-hook-form"
import { useIntl } from "react-intl"
import { CountrySelect } from "./CoutrySelect"
import "./style.sass"

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string
  error?: string
  helper?: string
  disabled?: boolean
  className?: string
  classNames?: { input: string }
  isPinField?: boolean
  isNumberField?: boolean
  mode?: string
  rows?: number
  uppercase?: boolean
}
export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ mode, uppercase, isPinField, className, classNames, rows, isNumberField, label, disabled, error, ...props }, ref) => {
    const Component = rows != null ? "textarea" : "input"
    const pattern = useMemo(() => {
      let temp = mode
        ?.split("")
        .map((type) => {
          switch (type) {
            case "w":
              return "\\W"
            case "a":
              return "a-zA-Z\\s"
            case "n":
              return "0-9"
            default:
              return null
          }
        })
        .filter(Boolean)
        .join("")
      return new RegExp(`[^${temp}]`, "g")
    }, [mode])
    return (
      <div
        className={cn("custom-input", className, {
          "custom-input__disabled": disabled,
        })}
      >
        {label != null && <span className="custom-input__label">{label}</span>}
        <Component
          {...props}
          disabled={disabled}
          // @ts-ignore
          ref={ref}
          rows={rows}
          className={cn("custom-input__input", classNames?.input, error && "custom-input__error-input")}
          maxLength={isPinField ? 4 : props.maxLength}
          onInput={(e) => {
            props.onInput?.(e as React.FormEvent<HTMLInputElement>)
            if ((isPinField || isNumberField) && mode == null) {
              const input = e.currentTarget
              let value = input.value
              value = value.replace(/[^0-9.]/g, "")
              const firstDotIndex = value.indexOf(".")
              if (firstDotIndex !== -1) {
                value = value.substring(0, firstDotIndex + 1) + value.substring(firstDotIndex + 1).replace(/\./g, "") // faqat 1 dona nuqta qoldiriladi
              }
              if (value.startsWith(".")) {
                value = "0" + value
              }
              input.value = value
              return
            }
            if (mode != null) {
              e.currentTarget.value = e.currentTarget.value.replace(pattern, "")
            }
            e.currentTarget.value = e.currentTarget.value.replace(/[А-Яа-яЁё]*$/gi, "")
            if (uppercase) e.currentTarget.value = e.currentTarget.value.toUpperCase()
          }}
        />
        {(error != null || props.helper != null) && <span className={clsx(error != null && "custom-input__error")}>{error || props.helper}</span>}
      </div>
    )
  },
) as CompoundedComponent<InputProps, HTMLInputElement, { Form: typeof FormInput; Phone: typeof FormPhoneInput }>

type IFormInput<T extends FieldValues> = IFormControl<T> & InputProps

const FormInput = <T extends FieldValues>({ control, name, rules, required, ...props }: IFormInput<T>) => {
  const { $t: t } = useIntl()
  const extendedRules: RegisterOptions<T> = {
    ...rules,
    required: { value: required ?? false, message: t({ id: "required" }) },
    ...(props.isPinField && !rules?.validate
      ? {
          validate: (value, values) => {
            if (/(\d)\1{3}/.test(value)) return t({ id: "identical-pin" })
            if (/(1234|2345|3456|4567|5678|6789|9876|8765|7654|6543|5432|4321)/.test(value)) return t({ id: "sequnce" })
            if (rules?.validate != null && typeof rules.validate === "function") return rules.validate?.(value, values)
          },
        }
      : {}),
  }
  return (
    <Controller
      {...{ control, name, rules: extendedRules }}
      render={({ field, fieldState: { error } }) => <Input {...props} {...field} error={error?.message} />}
    />
  )
}
type IFormPhoneInputPorps<T extends FieldValues> = InputProps & IFormControl<T> & { withFlag?: boolean; countryCodeName?: Path<T> }
const FormPhoneInput = <T extends FieldValues>({ control, name, rules, withFlag = false, countryCodeName, ...props }: IFormPhoneInputPorps<T>) => {
  const { $t: t } = useIntl()
  const [selected, setSelected] = useState("")
  const formContext = useFormContext<T>()
  return (
    <Controller
      {...{ control, name }}
      rules={{
        ...rules,
        validate: (value) => {
          const { data } = getFormattedPhone(value || "")
          const isValid = isValidPhoneNumber(value || "+44")
          if (countryCodeName != null && data?.country != null && isValid) {
            formContext?.setValue(countryCodeName, data.country as any, { shouldValidate: true, shouldDirty: true })
          }
          return !isValid ? t({ id: "invalid-phone" }) : undefined
        },
      }}
      render={({ field, fieldState: { error } }) => {
        const { formatted, data } = getFormattedPhone((field.value as string) || "+44")
        return (
          <div
            className={clsx("flex gap-2.5 w-full form-phone-input", {
              "form-phone-input__error": Boolean(error?.message),
            })}
          >
            {withFlag && (
              <CountrySelect
                withDialCode
                disabled={props.disabled}
                value={data?.country?.toLowerCase() ?? selected}
                onlyFlag
                searchable
                onChange={(value) => {
                  setSelected(value.value)
                  formContext?.setFocus(name)
                  field.onChange(`+${value.meta?.dialCode}`)
                }}
              />
            )}
            <Input
              {...props}
              error={error?.message}
              {...field}
              value={formatted}
              onChange={(e) => {
                if (e.target.value.length === 0) setSelected("")
                const phone = e.currentTarget.value?.replace(/ /g, "")
                const { data } = getFormattedPhone(phone)
                field.onChange(phone)
                if (countryCodeName != null && data?.country != null && isValidPhoneNumber(phone || "+44")) {
                  formContext?.setValue(countryCodeName, data.country as any, { shouldValidate: true, shouldDirty: true })
                }
              }}
              className="w-full"
            />
          </div>
        )
      }}
    />
  )
}
Input.Form = FormInput
Input.Phone = FormPhoneInput
